'use client'

import { useEffect, useRef } from 'react'

import { accentAt } from '@/lib/brand-tones'
import { LOGO_SVG, LOGO_VIEWBOX } from '@/lib/logo-svg'
import { cn } from '@/lib/utils'

/**
 * The FLOU mark, assembling itself out of a dust cloud.
 *
 * React Bits' `ParticleText` samples a STRING: it sets type on an offscreen
 * canvas, walks the alpha channel and turns every opaque pixel into a target.
 * This samples the wordmark's own SVG instead. The difference is the whole
 * point — sampling text would have assembled the letters F, L, O and U in
 * whatever face the heading happens to use, and FLOU's mark is a set of drawn
 * outlines, not four glyphs. What settles here is the logo from
 * `lib/logo-svg.ts`, the same paths the header and the login page paint.
 *
 * ## IT WAITS FOR THE READER
 *
 * `trigger="mount"` is right for a component that opens a page. This one sits
 * two screens down, so on mount it would scatter and gather to an empty room
 * and the reader would arrive at a logo that had already happened. The gather
 * starts on intersection instead, like every other entrance on the site.
 *
 * ## THE TEXT IS STILL IN THE HEADING
 *
 * A canvas has no text in it, and this one is half of an `<h2>`. The heading
 * carries the full string in an `sr-only` span and everything visible —
 * including this — is `aria-hidden`, so the outline, the crawler and the
 * screen reader all still get `POZNAJ FLOU.` while the eye gets the dust.
 */

/** Gap between sampled pixels. Lower is denser, and quadratically dearer. */
const DENSITY = 3

/** Particle edge in CSS px. */
const SIZE = 2.1

/**
 * How far, relative to the mark's height, the cloud starts from its target.
 *
 * It MUST stay under the canvas padding below, and that is not a style choice.
 * The canvas is the only thing the particles can be drawn on, so a cloud that
 * starts wider than the canvas is a cloud with its corners cut off — which is
 * exactly what read as the mark "forming out of a square". `BLEED` is 0.89 of
 * the mark's height on every side; this is comfortably inside it.
 */
const SCATTER = 0.72

/**
 * How much bigger the canvas is than the mark, as a fraction of the MARK'S
 * HEIGHT on every side.
 *
 * It reaches the CSS as two DIFFERENT percentages, because a percentage inset
 * resolves against the height at the top and against the width at the side,
 * and this box is 3.574 times wider than it is tall. Dividing by the aspect
 * ratio is what makes the padding the same number of pixels the whole way
 * round; one percentage for both would have given the cloud three times as
 * much room sideways as above, and the top and bottom would clip while the
 * sides did not. Derived rather than written out so the two can never drift.
 */
const BLEED = 0.89

/**
 * A hash, not a counter.
 *
 * The previous version derived every particle's angle from `(i * 9301 + 49297)
 * % 233280`, walked in index order — which is a RAMP, not noise. Index order
 * is raster order, so consecutive particles came from neighbouring pixels and
 * left at neighbouring angles: the cloud swept rather than scattered, and the
 * sweep is the other half of what looked mechanical. This decorrelates them.
 */
function noise(n: number): number {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453123
  return x - Math.floor(x)
}

/** Convergence, in ms, plus the spread of per-particle head starts. */
const GATHER_MS = 1500
const STAGGER_MS = 460

/** Cursor pushback: strength in px, radius in px. */
const REPEL = 34
const REPEL_RADIUS = 110

/** Resting sway once the mark has formed, in px. */
const DRIFT = 0.7

/** Hard ceiling on particle count, whatever the box size implies. */
const MAX_PARTICLES = 4200

const LOGO_ASPECT = LOGO_VIEWBOX.width / LOGO_VIEWBOX.height

/** {@link BLEED}, as the `inset` shorthand's vertical and horizontal halves. */
const BLEED_INSET = `${(-BLEED * 100).toFixed(0)}% ${((-BLEED / LOGO_ASPECT) * 100).toFixed(0)}%`

// Sampling only reads the alpha channel, so the fill colour is irrelevant —
// the file's own black is left alone rather than swapped for white.
const LOGO_URI = `data:image/svg+xml;utf8,${encodeURIComponent(LOGO_SVG)}`

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max)
const easeOutCubic = (t: number) => 1 - (1 - t) ** 3

type Particle = {
  x: number
  y: number
  fromX: number
  fromY: number
  toX: number
  toY: number
  size: number
  color: string
  seed: number
  depth: number
  delay: number
}

export function ParticleWordmark({ className }: { className?: string }) {
  const hostRef = useRef<HTMLSpanElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const host = hostRef.current
    const canvas = canvasRef.current
    if (!host || !canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    let particles: Particle[] = []
    let frame = 0
    let resample = 0
    let build = 0
    let width = 0
    let height = 0
    let gathering = false
    let gatherFrom = 0
    /** Latched by the observer. Until it flips, the cloud is drawn but held. */
    let armed = reduced
    /** Live: on screen or not. The drift is only owed to a reader looking. */
    let visible = false
    let disposed = false

    const pointer = { on: false, x: 0, y: 0, smoothX: 0, smoothY: 0 }

    const draw = (now: number) => {
      ctx.clearRect(0, 0, width, height)
      pointer.smoothX += (pointer.x - pointer.smoothX) * 0.18
      pointer.smoothY += (pointer.y - pointer.smoothY) * 0.18

      let settled = true

      for (const p of particles) {
        let x = p.toX
        let y = p.toY
        let progress = 1

        if (gathering) {
          progress = clamp((now - gatherFrom - p.delay) / GATHER_MS, 0, 1)
          const eased = easeOutCubic(progress)
          x = p.fromX + (p.toX - p.fromX) * eased
          y = p.fromY + (p.toY - p.fromY) * eased
          if (progress < 1) settled = false
        } else if (!armed) {
          // Held: sitting where it was scattered, waiting to be seen.
          x = p.fromX
          y = p.fromY
          progress = 0
        } else if (!reduced) {
          const t = now * 0.001
          x += Math.sin(t * 0.9 + p.seed * 10) * DRIFT * p.depth
          y += Math.cos(t * 0.75 + p.depth * 10) * DRIFT * p.depth
        }

        if (pointer.on && !reduced) {
          const dx = x - pointer.smoothX
          const dy = y - pointer.smoothY
          const distance = Math.hypot(dx, dy)
          if (distance > 0 && distance < REPEL_RADIUS) {
            const force = (1 - distance / REPEL_RADIUS) ** 2 * REPEL
            x += (dx / distance) * force
            y += (dy / distance) * force
          }
        }

        // Still in flight — after a cursor pushback the cloud has to be
        // allowed to fall back into the mark even though nothing is gathering.
        if (Math.abs(x - p.x) > 0.05 || Math.abs(y - p.y) > 0.05) settled = false

        const follow = reduced ? 1 : 0.22
        p.x += (x - p.x) * follow
        p.y += (y - p.y) * follow

        ctx.globalAlpha = clamp(0.3 + progress * 0.7, 0, 1)
        ctx.fillStyle = p.color
        ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size)
      }

      ctx.globalAlpha = 1
      if (gathering && settled) gathering = false

      // The loop is owed to what is on screen. Held (not yet armed), scrolled
      // past, or finished under reduced motion, there is nothing left to draw
      // and the frame is given back — a headline two screens up does not get
      // to own rAF for the rest of the session. The observer, a pointer move
      // or a resize starts it again.
      const owed = gathering || !settled || (armed && visible && !reduced)
      if (!owed) {
        frame = 0
        return
      }
      frame = requestAnimationFrame(draw)
    }

    const run = () => {
      if (!frame && !disposed) frame = requestAnimationFrame(draw)
    }

    const sample = async () => {
      const id = ++build
      const box = canvas.getBoundingClientRect()
      const mark = host.getBoundingClientRect()
      width = Math.floor(box.width)
      height = Math.floor(box.height)
      if (width <= 0 || height <= 0 || mark.height <= 0) return

      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = Math.max(1, Math.floor(width * dpr))
      canvas.height = Math.max(1, Math.floor(height * dpr))
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      // The mark is drawn at the size of the HOST box — the span that sits in
      // the heading's flow — while the canvas around it is deliberately
      // larger. The surplus is where the cloud lives before it gathers; sized
      // to the canvas instead, the outermost particles would start clipped.
      const markHeight = mark.height
      const markWidth = markHeight * LOGO_ASPECT

      const image = new Image()
      image.src = LOGO_URI
      try {
        await image.decode()
      } catch {
        return
      }
      if (id !== build || disposed) return

      const off = document.createElement('canvas')
      off.width = Math.max(1, Math.round(markWidth))
      off.height = Math.max(1, Math.round(markHeight))
      const offCtx = off.getContext('2d', { willReadFrequently: true })
      if (!offCtx) return
      offCtx.drawImage(image, 0, 0, off.width, off.height)

      const pixels = offCtx.getImageData(0, 0, off.width, off.height).data
      const originX = width / 2 - off.width / 2
      const originY = height / 2 - off.height / 2
      const step = Math.max(2, DENSITY)

      const targets: Array<{ x: number; y: number; t: number }> = []
      for (let y = 0; y < off.height; y += step) {
        for (let x = 0; x < off.width; x += step) {
          if (pixels[(y * off.width + x) * 4 + 3] > 40) {
            targets.push({ x: originX + x, y: originY + y, t: x / off.width })
          }
        }
      }

      const stride = Math.max(1, Math.ceil(targets.length / MAX_PARTICLES))
      const spread = reduced ? 0 : markHeight * SCATTER

      particles = targets
        .filter((_, i) => i % stride === 0)
        .map((target, i) => {
          const seed = noise(i + 1)
          const spin = noise(i + 1013.7)
          const reach = noise(i + 2027.3)
          const depth = 0.45 + noise(i + 3041.9) * 0.9

          // A disc, and `sqrt` is what makes it one: a uniform radius packs the
          // particles into the middle and leaves a halo, which is the other
          // thing that reads as a shape rather than as dust. Stretched 2.2×
          // sideways because the mark is 3.574 times wider than it is tall — a
          // circular cloud around a long wordmark is a cloud that is thin at
          // the ends and piled up over the middle.
          const angle = spin * Math.PI * 2
          const radius = spread * (0.18 + Math.sqrt(reach) * 0.82)
          const fromX = target.x + Math.cos(angle) * radius * 2.2
          const fromY = target.y + Math.sin(angle) * radius
          return {
            x: reduced ? target.x : fromX,
            y: reduced ? target.y : fromY,
            fromX,
            fromY,
            toX: target.x,
            toY: target.y,
            size: SIZE,
            // Along the mark, left to right — the same ramp `ACCENT_TEXT`
            // clips through the words either side of it, so the logo and the
            // headline it sits in are lit by one gradient rather than two.
            color: accentAt(target.t),
            seed,
            depth,
            delay: reduced ? 0 : seed * STAGGER_MS,
          }
        })

      pointer.x = width / 2
      pointer.y = height / 2
      pointer.smoothX = pointer.x
      pointer.smoothY = pointer.y

      if (armed && !reduced) {
        gatherFrom = performance.now()
        gathering = true
      }
      run()
    }

    const queue = () => {
      if (resample) cancelAnimationFrame(resample)
      resample = requestAnimationFrame(() => {
        resample = 0
        void sample()
      })
    }

    // On the WINDOW, not on the canvas: the canvas is `pointer-events-none` so
    // that it cannot swallow clicks or text selection on whatever it overlaps,
    // and an element that takes no pointer events gets no pointer events of
    // its own either. The proximity test is what keeps this from being a
    // window-wide mousemove handler that does work on every frame of every
    // cursor move anywhere on the page.
    const onMove = (event: PointerEvent) => {
      if (!visible) return
      const box = canvas.getBoundingClientRect()
      const x = event.clientX - box.left
      const y = event.clientY - box.top
      const near =
        x > -REPEL_RADIUS &&
        y > -REPEL_RADIUS &&
        x < box.width + REPEL_RADIUS &&
        y < box.height + REPEL_RADIUS
      if (!near) {
        if (pointer.on) {
          pointer.on = false
          run()
        }
        return
      }
      pointer.x = x
      pointer.y = y
      pointer.on = true
      run()
    }
    const onLeave = () => {
      pointer.on = false
      run()
    }

    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('blur', onLeave)

    const resize = new ResizeObserver(queue)
    resize.observe(host)

    // Two jobs, one observer: it ARMS the gather the first time the mark is
    // reached, and from then on it is the on-screen flag the frame loop reads.
    const seen = new IntersectionObserver(
      ([entry]) => {
        visible = entry?.isIntersecting ?? false
        if (visible && !armed) {
          armed = true
          if (particles.length) {
            gatherFrom = performance.now()
            gathering = true
          }
        }
        if (visible) run()
      },
      { rootMargin: '0px 0px -12% 0px' },
    )
    seen.observe(host)

    void sample()

    return () => {
      disposed = true
      build += 1
      resize.disconnect()
      seen.disconnect()
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('blur', onLeave)
      if (frame) cancelAnimationFrame(frame)
      if (resample) cancelAnimationFrame(resample)
    }
  }, [])

  return (
    // The HOST is the mark's own box, so the heading lays out around a FLOU of
    // exactly the right width. The canvas breaks out of it on every side to
    // give the cloud room, and takes no part in the layout.
    <span
      ref={hostRef}
      aria-hidden="true"
      className={cn('relative inline-block aspect-[434/121]', className)}
    >
      {/* A SPAN does the stretching, not the canvas. A canvas is a replaced
          element: with `width: auto` it takes its intrinsic size from its own
          `width` attribute and the over-constrained inset equation drops the
          `right` offset — so `inset: -46% -13%` positioned it correctly and
          then let it stay 1200×600. The wrapper is not replaced, so the
          negative insets size it the way they read, and `h-full w-full` on the
          canvas is a percentage rather than `auto`. */}
      <span
        className="pointer-events-none absolute block"
        // `BLEED` on every side, in pixels — see the note on that constant
        // for why the two percentages differ. `pointer-events-none` because
        // this box is nearly three times the height of the mark: left
        // clickable it covers the headline beside it and swallows its text
        // selection, for a cursor effect that works just as well off a window
        // listener.
        style={{ inset: BLEED_INSET }}
      >
        <canvas ref={canvasRef} className="block h-full w-full" />
      </span>
    </span>
  )
}
